import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { FormControl, Tab, Tabs, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Feature, FeatureCollection } from 'geojson';
import { nanoid } from 'nanoid';
import { addSavedLocationShape } from '../api/locations';
import { LocationsContext } from '../context/locations';
import { LocationShapes, LocationShapesSourceTypes } from '../core/global';
import { createPointShape } from '../data/form-shapes';
import { extractLatLon } from '../helpers/common';
import { isNumber } from '../helpers/core';
import { ErrorMessage } from './error-message';
import { CreateForm } from './form-components';
import Loader from './loader';
import { MapPreview } from './map-preview';
import { Uploader } from './uploader';

const emptyFile = {
  content: null, 
  type: null
}

interface CreateSavedLocationsShapeProps {
  uid: number | null
  onClose: () => void
}
interface Methods {
  name: string
  key: 'upload' | 'manual' | 'shape'
}

export const methods: Methods[] = [
  {
    name: 'GIS shape file',
    key: 'shape'
  },
  {
    name: 'File upload',
    key: 'upload'
  },
  {
    name: 'Manual entry',
    key: 'manual'
  }
]
export const CreateSavedLocationsShape = ({ uid, onClose }: CreateSavedLocationsShapeProps) => {
  const [tabIndex, setTabIndex] = React.useState(0);
  const [title, setTitle] = useState('')
  // const [locationError, setLocationError] = useState<string[]>([]);
  // const [showMessage, setShowMessage] = useState(false)
  // const [notes, setNotes] = useState('')
  const [uploaderError, setUploaderError] = useState<string[]>([])
  // const [modalActive, setModalActive] = useState(false)
  const [shapeFileData, setShapeFileData] = useState<{content: FeatureCollection | null, type: LocationShapesSourceTypes | null}>(emptyFile)
  const [lat, setLat] = useState<number | null>(null)
  const [lon, setLon] = useState<number | null>(null)
  const [latString, setLatString] = useState<string | null>(null)
  const [lonString, setLonString] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  
  useEffect(() => {
    const extractedLocation = extractLatLon(`${latString} ${lonString}`)
    let _lat = null
    let _lon = null
    if (extractedLocation) {
      const {location: {lat, lng}} = extractedLocation
      _lat = Number(lat)
      _lon = Number(lng)
    }

    setLat(_lat)
    setLon(_lon)
  }, [latString, lonString])
  
  useEffect(() => {
    setUploaderError([])
    setShapeFileData(emptyFile)
    setLat(null)
    setLon(null)
  }, [tabIndex])

  const { 
    setNotSavedYetLocationShapes, 
    refetchLocationShapes 
  } = useContext(LocationsContext);

  useEffect(() => {
    refetchLocationShapes(uid)
  }, [uid])

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const onSave = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault()
    setLoading(true)
    const execute = async () => {
      const useLat = lat
      const useLon = lon
      const source = shapeFileData?.type || 'manual'

      const {features} = shapeFileData?.content || {
        // @ts-ignore
        "type": "FeatureCollection",
        "features": [
          {
            "type": "Feature",
            "geometry": {
              "type": "Point",
              "coordinates": [useLon, useLat]
            },
            "properties": {}
          }
        ]
      }

      if (uid) {
        setUploaderError([])
        for (let index = 0; index < features.length; index++) {
          // @ts-ignore
          const f: Feature = features[index];
          const newFeature: FeatureCollection = {
            "type": "FeatureCollection",
            "features": [
              f
            ]}
          let useTitle = index > 0 ? `${title}-${index}` : title
          if (f  && f?.properties) {
            const {properties} = f
            useTitle = properties.name || properties.NAME || useTitle
          }
          const { error, message} = await addSavedLocationShape({
            uid, 
            title: useTitle, 
            data: JSON.stringify(newFeature),
            source
          })

          
          if (error !== 200) {
            setUploaderError( e => [...e, `${message}`])
          }
        }
        onClose()
      } else {
        setNotSavedYetLocationShapes((loc: LocationShapes[] | null) => {
          const savedLoc = loc !== null ? [...loc] : []

          const allFeatures: FeatureCollection[] = features.map((f) => {
            const newFeature: FeatureCollection = {
              "type": "FeatureCollection",
              "features": [
                f as Feature
              ]}
            return newFeature
          })

          return [
            ...savedLoc,
            ...allFeatures.map((feature:FeatureCollection, index: number) => {
              let useTitle = index > 0 ? `${title}-${index}` : title
              if (feature  && feature?.features && Array.isArray(feature?.features)) {
                const [firstRow] = feature.features
                const {properties} = firstRow || {properties: null}
                useTitle = (properties && (properties.name || properties.NAME)) || useTitle
              }
              return {
                uid: `new_${nanoid()}`,
                created_at: dayjs().toString(),   //format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
                data: feature,
                type: 'geo-json',
                source,
                title: useTitle,
              }
            })
          ]
        })
        setUploaderError([])
        setLoading(false)
        onClose()
      }
    }
    execute()
  }, [uid, shapeFileData, title, lon, lat])

  // const onClose = () => {
  //   setShowMessage(false)
  //   setLocationError([])    
  //   handleClose()
  // }

  // useEffect(() => {
  //   setShowMessage(false)
  //   setLocationError([])
  // }, [])

  // useEffect(() => {
  //   if (data === null) {
  //     setTitle('')
  //     setNotes('')
  //   } else {
  //     setTitle(data?.title || '')
  //     setNotes(data.notes || '')
  //     // @ts-ignore
  //     data.uid
  //   }
  // }, [data])

  // const validateInputs = () => {
  //   let isValid = true;
  //   if (!title) {
  //     isValid = false;
  //     setLocationError( e => [...e, `You must name your location to save it.`])
  //   }
  //   return isValid;
  // };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // const isValid = validateInputs()
    // setLocationError([])

    // if (isValid && data !== null) {
    //   const execute = async () => {
    //     let result
    //     if (Object.prototype.hasOwnProperty.call(data, 'uid')) {
    //       result = await updateLocation({
    //         notes,
    //         title,
    //         // @ts-ignore
    //         uid: data.uid
    //       })  
    //     } else {
    //       result = await addLocation({
    //         notes,
    //         title
    //       })
  
    //     }
    //     const {error, message, response} = result
    //     if (error !== 200) {
    //       setLocationError( e => [...e, `${message}`])
    //     } else {
    //       onClose()
    //     }
    //   }
    //   execute()
    // }
  };

  if (uid === null) return null

  return (
    <>
      <Dialog
        fullWidth
        open
        onClose={onClose}
      >
        <DialogTitle>Region of interest</DialogTitle>
        {loading ?
            <Loader center />
          :
            <DialogContent
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
            >
              <DialogContentText>
                Upload a shape file or CSV file representing X and Y polygon locations to create a new region of interest. You can also enter location manually.
              </DialogContentText>
              <FormControl>
                <TextField
                  id="title"
                  name="title"
                  label="Location Shape name"
                  placeholder="Location shape name"
                  value={title}
                  autoFocus
                  required
                  fullWidth
                  variant="outlined"
                  onChange={(event: React.SyntheticEvent) => {
                    // @ts-ignore
                    setTitle(event.currentTarget.value)
                  }}
                  // color={titleError ? 'error' : 'primary'}
                  sx={{ ariaLabel: 'title' }}
                />
              </FormControl>
              <Tabs
                // className={styles.tabs}
                value={tabIndex}
                onChange={handleTabChange}
                allowScrollButtonsMobile
              >
                {methods.map( method => {
                  const {key, name} = method
                  return <Tab key={key} label={name} />
                })}
              </Tabs>
              { tabIndex === 0 && <>
                <Uploader 
                  isShapeFileUpload
                  onUploadCompleted={(content, type) => {
                    if (content === null) {
                      const message = 'Error uploading your shape file(s), please try again...'
                      setUploaderError( e => [...e, `${message}`])
                    } else {
                      setUploaderError([])
                    }
                    setShapeFileData({
                      content,
                      type
                    })
                  }}
                />
                <ErrorMessage message={uploaderError} />
              </>}
              { tabIndex === 1 && <>
                <Uploader onUploadCompleted={(content, type) => {
                  if (content === null) {
                    const message = 'Error uploading your file, please try again...'
                    setUploaderError( e => [...e, `${message}`])
                  } else {
                    setUploaderError([])
                  }
                  setShapeFileData({
                    content,
                    type
                  })
                }} />
                <ErrorMessage message={uploaderError} />
              </>}
              {tabIndex === 2 && <>
                <CreateForm
                  size="medium"
                  allowEdit={false}
                  shape={createPointShape}
                  onChange={(vals, key) => {
                    const {lat, lon} = vals
                    setLatString(lat)
                    setLonString(lon)
                  }}
                  // successMessage={id === 0 ? "Your billing address has been created." : "Your billing address has been updated."}
                  defaultValues={{
                    lat: latString,
                    lon: lonString,
                  }}
                />
                <div style={{margin: "0"}}>
                  <MapPreview 
                    onClick={(lat: string, lon: string) => {
                      if (isNumber(lat) && isNumber(lon)) {
                        setLatString(lat)
                        setLonString(lon)
                      }
                    }}
                    // key={`${lon}-${lat}`} 
                    width={552} 
                    lat={Number(lat)} 
                    lon={Number(lon)} 
                  />
                </div>
              </>}

            </DialogContent>

        }
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button disabled={loading} onClick={onClose}>Cancel</Button>
          <Button 
            variant="contained"
            type="submit"
            onClick={onSave}
            disabled={loading || !!(!title || !(shapeFileData.content || (isNumber(lon) && isNumber(lat))))}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

